import { useCallback, useEffect, useState } from "react";
import { LayoutTemplate, Trash2, BookmarkPlus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { toPayload } from "@/components/roster/RosterEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const DAY = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const SaveDialog = ({ open, shifts, onClose, onSaved }) => {
  const [name, setName] = useState("");
  useEffect(() => {
    if (open) setName("");
  }, [open]);
  const save = async () => {
    try {
      await api.post("/roster-templates", { name, shifts: toPayload(shifts) });
      toast.success("Template saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm" data-testid="save-template-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl text-[#24474A]">Save as template</DialogTitle>
          <DialogDescription>Saves the days, hours, location and notes shown for this week.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5"><Label>Template name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Standard week" data-testid="template-name-input" /></div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" className="rounded-full" onClick={onClose}>Cancel</Button>
          <Button className="rounded-full" disabled={!name.trim()} onClick={save} data-testid="template-save-btn">Save template</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const RosterTemplates = ({ shifts, disabled, onApply }) => {
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => api.get("/roster-templates").then((r) => setTemplates(r.data)), []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (e, t) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    await api.delete(`/roster-templates/${t.id}`);
    toast.success("Template deleted");
    load();
  };
  const apply = (t) => {
    if (shifts.length && !window.confirm(`Replace this week's shifts with "${t.name}"?`)) return;
    onApply(t.id);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="rounded-full" disabled={disabled} data-testid="roster-templates-btn"><LayoutTemplate className="w-4 h-4 mr-1.5" /> Templates</Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72" data-testid="roster-templates-menu">
          <DropdownMenuLabel>Apply a template</DropdownMenuLabel>
          {templates.map((t) => (
            <DropdownMenuItem key={t.id} onClick={() => apply(t)} className="flex items-center gap-2 py-2" data-testid={`template-apply-${t.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate">{t.name}</div>
                <div className="text-[11px] text-slate-500 truncate">{t.days.map((d) => DAY[d.weekday]).join(", ")} · {new Set(t.days.map((d) => `${d.start}–${d.end}`)).size > 1 ? "mixed hours" : `${t.days[0]?.start}–${t.days[0]?.end}`}</div>
              </div>
              <button onClick={(e) => remove(e, t)} className="p-1 rounded hover:bg-rose-50" data-testid={`template-delete-${t.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></button>
            </DropdownMenuItem>
          ))}
          {templates.length === 0 && <div className="px-2 py-3 text-sm text-slate-500">No templates yet.</div>}
          <DropdownMenuSeparator />
          <DropdownMenuItem disabled={!shifts.length} onClick={() => setSaving(true)} data-testid="template-save-current-btn">
            <BookmarkPlus className="w-4 h-4 mr-2" /> Save this week as template
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <SaveDialog open={saving} shifts={shifts} onClose={() => setSaving(false)} onSaved={load} />
    </>
  );
};
